import type { tInspectionFormInput } from '../types';
import type { ServiceProcedureConsumable } from '../types/companyResourceTypes';
import type { ExpenseCategoryEntry, PmPartPriceSnapshot } from '../types/costTrackingTypes';
import type { PMPartsAndProcedure } from '../types/pmTypes';
import { OPERATOR_RESPONSE_TYPES } from './constants';
import { linkedConsumablesForProcedure } from './companyResourceUtils';
import { costCategoryFromItem, groupPartSnapshotsByCostCategory } from './costTrackingUtils';
import { isEnteredQuantityValue, OPERATOR_RESPONSE_VALUES } from './formPromptMapping';

export const DAILY_SERVICE_EXPENSE_CATEGORY = 'Daily Service';
export const DAILY_SERVICE_WORK_DESCRIPTION_PREFIX = 'Daily Service';

export type DailyServiceProcedureCostLine = NonNullable<tInspectionFormInput['procedureCostLines']>[number];

export function isDailyServiceCostTrackingEnabled(
    company?: { costTrackingModuleEnrollment?: boolean | null } | null,
    unit?: { costTrackingModuleEnrollment?: boolean | null } | null,
): boolean {
    return Boolean(company?.costTrackingModuleEnrollment && unit?.costTrackingModuleEnrollment);
}

function catalogItemPrice(item: PMPartsAndProcedure | null | undefined): number {
    return Number(item?.price) || 0;
}

export function attachDailyServiceCostSnapshots(
    prompts: tInspectionFormInput[],
    catalog: PMPartsAndProcedure[],
    links: ServiceProcedureConsumable[],
): tInspectionFormInput[] {
    const catalogById = new Map(
        catalog.filter((item) => item?.id).map((item) => [item.id, item]),
    );

    return prompts.map((prompt) => {
        if (prompt.serviceItemId) {
            const item = catalogById.get(prompt.serviceItemId);
            if (!item) return prompt;
            return {
                ...prompt,
                unitPrice: catalogItemPrice(item),
                defaultUM: prompt.defaultUM || item.unitOfMeasure || '',
                ...costCategoryFromItem(item),
            };
        }

        if (!prompt.serviceProcedureId) return prompt;

        const procedureCostLines: DailyServiceProcedureCostLine[] = linkedConsumablesForProcedure(
            links,
            catalog,
            prompt.serviceProcedureId,
        )
            .map(({ link, item }) => {
                const quantity = Number(link.quantity) || 0;
                if (quantity <= 0 || !item?.id) return null;
                return {
                    serviceItemId: item.id,
                    itemName: item.itemName,
                    quantity,
                    unitPrice: catalogItemPrice(item),
                    ...costCategoryFromItem(item),
                };
            })
            .filter((line): line is DailyServiceProcedureCostLine => Boolean(line));

        return { ...prompt, procedureCostLines };
    });
}

function isBillableQuantityPrompt(prompt: tInspectionFormInput): boolean {
    if (prompt.subType && prompt.subType !== OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY) {
        return false;
    }
    return isEnteredQuantityValue(prompt.value);
}

function isAcknowledgedProcedure(prompt: tInspectionFormInput): boolean {
    return (
        prompt.subType === OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT &&
        prompt.value === OPERATOR_RESPONSE_VALUES.acknowledged
    );
}

export function buildDailyServiceCostSnapshots(prompts: tInspectionFormInput[]): PmPartPriceSnapshot[] {
    const snapshots: PmPartPriceSnapshot[] = [];

    for (const prompt of prompts) {
        if (isBillableQuantityPrompt(prompt) && prompt.serviceItemId) {
            const quantity = Number(prompt.value);
            const unitPrice = Number(prompt.unitPrice) || 0;
            const lineTotal = quantity * unitPrice;
            if (lineTotal <= 0) continue;
            snapshots.push({
                serviceItemId: prompt.serviceItemId,
                itemName: prompt.label || 'Consumable',
                quantity,
                unitPrice,
                lineTotal,
                ...costCategoryFromItem(prompt),
            });
            continue;
        }

        if (!isAcknowledgedProcedure(prompt)) continue;
        for (const line of prompt.procedureCostLines || []) {
            const lineTotal = line.quantity * line.unitPrice;
            if (lineTotal <= 0) continue;
            snapshots.push({
                serviceItemId: line.serviceItemId,
                itemName: line.itemName,
                quantity: line.quantity,
                unitPrice: line.unitPrice,
                lineTotal,
                ...costCategoryFromItem(line),
            });
        }
    }

    return snapshots;
}

export function dailyServiceExpenseWorkDescription(reportId: string, unitName?: string | null): string {
    const unitPart = unitName?.trim() ? ` · ${unitName.trim()}` : '';
    return `${DAILY_SERVICE_WORK_DESCRIPTION_PREFIX}${unitPart} · ${reportId}`;
}

export function isDailyServiceExpenseForReport(
    expense: { invoiceNumber?: string | null; workDescription?: string | null },
    reportId: string,
): boolean {
    if (!reportId) return false;
    if (expense.invoiceNumber === reportId) return true;
    return Boolean(expense.workDescription?.includes(reportId));
}

export function buildDailyServiceExpenseInput(args: {
    companyId: string;
    unitId: string;
    equipmentCategoryId?: string | null;
    reportId: string;
    unitName?: string | null;
    dateOfService: string;
    snapshots: PmPartPriceSnapshot[];
}): Record<string, unknown> | null {
    const { unassignedTotal, entries } = groupPartSnapshotsByCostCategory(args.snapshots);
    const categoryEntries: ExpenseCategoryEntry[] = [...entries];
    if (unassignedTotal > 0) {
        categoryEntries.push({
            categoryId: '',
            categoryName: DAILY_SERVICE_EXPENSE_CATEGORY,
            cost: unassignedTotal,
        });
    }
    const total = unassignedTotal + entries.reduce((sum, entry) => sum + entry.cost, 0);
    if (total <= 0 || !args.companyId || !args.unitId) return null;

    return {
        companyId: args.companyId,
        unitId: args.unitId,
        equipmentCategoryId: args.equipmentCategoryId || null,
        dateOfInput: args.dateOfService,
        dateOfService: args.dateOfService,
        workDescription: dailyServiceExpenseWorkDescription(args.reportId, args.unitName),
        invoiceNumber: args.reportId,
        pmExpense: 0,
        pmPartsSnapshot: JSON.stringify(args.snapshots),
        expenseCategories: JSON.stringify(categoryEntries),
        laborHoursST: 0,
        laborHoursOT: 0,
        laborRateST: 0,
        laborRateOT: 0,
        totalLaborCost: 0,
        totalExpense: total,
    };
}
